import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import { useToast } from '../../components/ui/use-toast';
import { useRestaurant } from '../../context/RestaurantContext';
import { ClipboardList, Plus, Layers, FileText, ChevronLeft, CheckCircle2, Clock } from 'lucide-react';
import { listSets, listStocktakes, createStocktake } from '../../services/liquorTrackingService';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'adhoc', label: 'Ad-hoc' },
];

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

export default function LiquorTrackingHome() {
  const navigate = useNavigate();
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const { toast } = useToast();

  const [sets, setSets] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null); // form state for the start dialog
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    try {
      const [s, st] = await Promise.all([
        listSets(restaurantId),
        listStocktakes(restaurantId, locationId),
      ]);
      setSets(s);
      setSessions(st);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to load liquor tracking', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, toast]);

  useEffect(() => { load(); }, [load]);

  const openStart = () => setStarting({ liquor_set_id: 'all', frequency: 'weekly', name: '', note: '' });

  const submitStart = async () => {
    setSaving(true);
    try {
      const payload = {
        frequency: starting.frequency,
        name: starting.name.trim(),
        note: starting.note.trim(),
      };
      if (starting.liquor_set_id && starting.liquor_set_id !== 'all') {
        payload.liquor_set_id = Number(starting.liquor_set_id);
      }
      const st = await createStocktake(restaurantId, locationId, payload);
      setStarting(null);
      navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/${st.id}`);
    } catch (e) {
      toast({ title: 'Error', description: e?.error || 'Failed to start the count', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 w-full">
      <button
        onClick={() => navigate(`/restaurant/${restaurantId}/liquor-tracking`)}
        className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4"
      >
        <ChevronLeft className="h-4 w-4" /> Locations
      </button>

      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl font-semibold text-foreground tracking-tight leading-none">Liquor Tracking</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/sets`)}>
            <Layers className="h-4 w-4 mr-2" /> Manage liquor sets
          </Button>
          <Button onClick={openStart}>
            <Plus className="h-4 w-4 mr-2" /> Start a count
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" /> Stocktakes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Set</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead className="text-center">Counted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
                )}
                {!loading && sessions.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No stocktakes yet. Start your first count.</TableCell></TableRow>
                )}
                {!loading && sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="whitespace-nowrap">{fmtDate(s.started_at)}</TableCell>
                    <TableCell>{s.name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{s.liquor_set_name || <span className="text-muted-foreground">All liquor</span>}</TableCell>
                    <TableCell className="capitalize">{s.frequency}</TableCell>
                    <TableCell className="text-center tabular-nums">{s.counted_items}</TableCell>
                    <TableCell>
                      {s.status === 'completed' ? (
                        <span className="inline-flex items-center gap-1 text-success text-sm"><CheckCircle2 className="h-4 w-4" /> Completed</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-600 text-sm"><Clock className="h-4 w-4" /> In progress</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {s.status === 'completed' ? (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/${s.id}/report`)}>
                          <FileText className="h-4 w-4 mr-1" /> Report
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/restaurant/${restaurantId}/locations/${locationId}/liquor-tracking/${s.id}`)}>
                          Continue
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Start-a-count dialog */}
      <Dialog open={starting !== null} onOpenChange={(o) => !o && setStarting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start a stocktake</DialogTitle>
          </DialogHeader>
          {starting && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Liquor set</Label>
                <Select value={String(starting.liquor_set_id)} onValueChange={(v) => setStarting({ ...starting, liquor_set_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All liquor at this location</SelectItem>
                    {sets.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name} ({s.item_count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cadence</Label>
                <Select value={starting.frequency} onValueChange={(v) => setStarting({ ...starting, frequency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Name (optional)</Label>
                <Input value={starting.name} placeholder="e.g. June month-end count" onChange={(e) => setStarting({ ...starting, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Note (optional)</Label>
                <Textarea value={starting.note} rows={2} onChange={(e) => setStarting({ ...starting, note: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setStarting(null)}>Cancel</Button>
            <Button onClick={submitStart} disabled={saving}>{saving ? 'Starting…' : 'Start count'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
